import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import type { SearchResult } from '@fint/validation';
import { useSwitchOrg } from '@/hooks/useSwitchOrg';
import { useAuthStore } from '@/stores/auth';
import { cn } from '@/lib/cn';
import { SearchResultsList } from './SearchResultsList';
import { useGlobalSearch } from './useGlobalSearch';

/**
 * Ctrl+K (⌘K) opens the search from anywhere. One listener, in the layout,
 * because the layout is the only thing on every page - and the panel it opens
 * is mounted once, whichever trigger opened it.
 */
export function useSearchShortcut(open: () => void) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // From a tablet up the search bar is always on screen and takes the
      // shortcut itself (SearchBar); this opens the full-screen sheet on a phone.
      if (window.matchMedia('(min-width: 640px)').matches) return;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        open();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);
}

/**
 * What opens the panel. A field on a wide sidebar - it reads as the search box
 * it is - and an icon on the rail and in the phone's top bar, where a text
 * field would be a few characters wide.
 */
export function SearchTrigger({ variant, onOpen, className }: { variant: 'field' | 'icon'; onOpen: () => void; className?: string }) {
  if (variant === 'icon') {
    return (
      <button
        type="button"
        onClick={onOpen}
        aria-label="Search (Ctrl+K)"
        title="Search (Ctrl+K)"
        data-testid="search-open"
        className={cn('flex size-11 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent', className)}
      >
        <Search className="size-4" aria-hidden />
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid="search-open"
      className={cn(
        'flex w-full items-center gap-2 rounded-md border border-border bg-background px-2.5 py-1.5 text-left text-sm text-muted-foreground hover:bg-accent touch:min-h-11',
        className,
      )}
    >
      <Search className="size-4 shrink-0" aria-hidden />
      <span className="flex-1 truncate">Search…</span>
      <kbd className="rounded border border-border px-1 text-[10px] font-medium">Ctrl K</kbd>
    </button>
  );
}

/**
 * The search itself: a panel near the top of the screen on a large one, the
 * whole screen on a phone. Portalled to <body> so no sticky bar or tab bar
 * paints over it (Desk's lesson: the phone tab bar is z-30 and swallowed taps
 * on the last result when the sheet was rendered in place).
 *
 * Picking a result in ANOTHER book switches to that book first - its records
 * are not in this one's pages - through `useSwitchOrg`, which also clears the
 * previous book's cached lists.
 */
export function SearchPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const switchOrg = useSwitchOrg();
  const activeOrgId = useAuthStore((s) => s.activeOrgId);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const s = useGlobalSearch(activeOrgId);

  // Focus on open - after the frame that mounts the input, or iOS keeps the
  // keyboard closed.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 50);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  if (!open) return null;

  const close = () => {
    onClose();
  };
  const pick = (r: SearchResult) => {
    onClose();
    s.reset();
    if (r.orgId !== activeOrgId) switchOrg(r.orgId, r.href);
    else navigate(r.href);
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center sm:bg-black/30 sm:p-4 sm:pt-[12vh]" onMouseDown={close}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Search"
        data-testid="search-panel"
        onMouseDown={(e) => e.stopPropagation()}
        className="flex h-full w-full flex-col bg-background sm:h-auto sm:max-h-[70vh] sm:max-w-xl sm:rounded-lg sm:border sm:border-border sm:shadow-xl"
      >
        <div className="flex items-center gap-2 border-b border-border px-3 py-2 pt-[max(0.5rem,var(--safe-top))] sm:pt-2">
          <Search className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          <input
            ref={inputRef}
            type="search"
            value={s.q}
            onChange={(e) => s.setQ(e.target.value)}
            onKeyDown={(e) => s.handleKeyDown(e, pick, close)}
            placeholder="Invoice no., customer, GSTIN, reference, ₹ amount…"
            aria-label="Search"
            data-testid="search-input"
            // 16px: iOS zooms the page into a focused input smaller than that.
            className="min-w-0 flex-1 bg-transparent py-1.5 text-base outline-none sm:text-sm"
          />
          <button
            type="button"
            onClick={close}
            aria-label="Close search"
            className="flex size-9 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent touch:size-11"
          >
            <X className="size-4" aria-hidden />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          <SearchResultsList
            groups={s.groups}
            flat={s.flat}
            highlight={s.highlight}
            onPick={pick}
            query={s.q}
            debounced={s.debounced}
            isFetching={s.isFetching}
            manyBooks={s.manyBooks}
            amount={s.data?.amount ? Number(s.data.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 }) : null}
          />
        </div>
        <p className="hidden border-t border-border px-3 py-1.5 text-[10px] text-muted-foreground sm:block">
          ↑ ↓ to move · Enter to open · Esc to close
        </p>
      </div>
    </div>,
    document.body,
  );
}
